import React from 'react';
import styled from 'styled-components';
import ProductCard from '@99designs/apps/spa/common/Product/ProductCard';
import ssoFacade from '@99designs/apps/spa/images/sso-facade.svg';
import ssoTable from '@99designs/apps/spa/images/sso-table.svg';
import { useProductsQuery } from '@99designs/apps/spa/pages/create/common/Products.generated';
import { ButtonWrapper } from '@99designs/apps/spa/pages/create/components/Button';
import ContentSetting from '@99designs/apps/spa/pages/create/components/ContentSetting';
import { isFixedPriceFragment } from '@99designs/apps/spa/pages/create/util/filterProducts';
import { Box, Button, Stack } from '@99designs/emu';
import { colors, typography } from '@99designs/emu/settings';
import { LoadableElement } from '@99designs/frontend-components/components/LoadableElement';
import { FormattedMessage } from '@99designs/frontend-components/i18n';
import { linkTo } from '@99designs/routing';
import { SquareProductLogo } from '@99designs/spa-common';
import { InlinedSvg } from '@99designs/spa-common';
import { Desktop } from '@99designs/spa-common';
import { ViewProductDetails } from '@99designs/spa-common';
import { packageToProductSummary } from '@99designs/spa-common';

const SsoTable = styled(InlinedSvg)`
    width: 700px;
    position: absolute;
    bottom: 16px;
    right: -480px;
`;

const SsoFacade = styled(InlinedSvg)`
    position: absolute;
    width: 450px;
    top: 112px;
    left: -384px;
`;

const Attribution = styled.div`
    position: absolute;
    bottom: 160px;
    right: -284px;
    font-family: ${typography.fontFamilies.attribution};
    color: ${colors.grey700};
    font-style: italic;
    font-size: ${typography.typeScale.size2}px;
`;

const CardContainer = styled.div`
    > * {
        background-color: white;
    }
`;

export function LandingPage() {
    const { error, data, loading } = useProductsQuery({
        variables: {
            input: {
                names: ['square-online-self-serve', 'square-online-self-serve-custom'],
            },
        },
    });

    if (error) {
        throw error;
    }

    if (loading) {
        return <LoadableElement variations={['fade-in']} isLoading={true} />;
    }

    if (!data) {
        throw new Error('package not found');
    }

    return (
        <ContentSetting>
            <Box marginTop={['none', '2bu']} position="relative">
                <Desktop>
                    <SsoFacade>{ssoFacade}</SsoFacade>
                    <SsoTable>{ssoTable}</SsoTable>
                    <Attribution>
                        <FormattedMessage
                            message={__placeholder('illustrations by {designerName}')}
                            values={{ designerName: 'wildanya' }}
                        />
                    </Attribution>
                </Desktop>
                <Stack space="1.5bu">
                    {data.products.map((p) => {
                        if (!isFixedPriceFragment(p)) {
                            return null;
                        }

                        const packageForProductSummary = {
                            title: p.title,
                            description: p.description,
                            amountInCents: p.pricing.amount.amountInCents,
                            currency: p.pricing.amount.currency,
                        };

                        return (
                            <CardContainer key={p.key}>
                                <ProductCard
                                    rightColumn={
                                        <Stack space="1bu">
                                            <Box display="flex" justifyContent="center">
                                                <ViewProductDetails
                                                    features={p.details}
                                                    additionalDetails={p.additionalDetails}
                                                    productSummary={packageToProductSummary({
                                                        ...packageForProductSummary,
                                                        logo: SquareProductLogo,
                                                    })}
                                                    image={p.image}
                                                />
                                            </Box>
                                            <ButtonWrapper>
                                                <Button
                                                    size="large"
                                                    appearance="primary"
                                                    width="fill"
                                                    href={linkTo.briefCreate(p.key)}
                                                    type="button"
                                                >
                                                    {__('Start brief')}
                                                </Button>
                                            </ButtonWrapper>
                                        </Stack>
                                    }
                                    productSummary={packageToProductSummary(
                                        packageForProductSummary
                                    )}
                                    features={p.details}
                                    hasLeftBorder={false}
                                />
                            </CardContainer>
                        );
                    })}
                </Stack>
            </Box>
        </ContentSetting>
    );
}
